import { getDerivedBlob, putDerivedBlob } from './persistence';

interface WorkerResponse { id: string; peaks?: Float32Array }

let worker: Worker | null = null;
const pending = new Map<string, (peaks: Float32Array) => void>();
const cache = new Map<string, Float32Array>();

function getWorker() {
  if (!worker) {
    worker = new Worker(new URL('../workers/media.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const resolve = pending.get(event.data.id);
      if (resolve && event.data.peaks) { pending.delete(event.data.id); resolve(event.data.peaks); }
    };
  }
  return worker;
}

export async function generateWaveform(projectId: string, assetId: string, blob: Blob, samples = 512): Promise<Float32Array | null> {
  const key = `${projectId}:${assetId}`;
  try {
    const context = new AudioContext();
    const audio = await context.decodeAudioData(await blob.arrayBuffer());
    const channels = Array.from({ length: audio.numberOfChannels }, (_, index) => audio.getChannelData(index).slice());
    await context.close();
    const id = crypto.randomUUID();
    const peaks = await new Promise<Float32Array>((resolve) => {
      pending.set(id, resolve);
      getWorker().postMessage({ id, type: 'waveform', channels, samples }, { transfer: channels.map((channel) => channel.buffer) });
    });
    cache.set(key, peaks);
    await putDerivedBlob(projectId, assetId, 'waveform', new Blob([peaks.buffer as ArrayBuffer], { type: 'application/octet-stream' }));
    return peaks;
  } catch { return null; }
}

export async function getWaveform(projectId: string, assetId: string): Promise<Float32Array | null> {
  const key = `${projectId}:${assetId}`;
  if (cache.has(key)) return cache.get(key)!;
  const blob = await getDerivedBlob(projectId, assetId, 'waveform');
  if (!blob) return null;
  const peaks = new Float32Array(await blob.arrayBuffer());
  cache.set(key, peaks);
  return peaks;
}
